/**
 * <cp-carousel> — carrossel leve das seções Cynthia Pamplona.
 * Usa rolagem nativa com scroll-snap (o layout fica no CSS da seção) e só sincroniza dots, setas e autoplay.
 *
 * Markup:
 *   <cp-carousel [autoplay="5"] [loop]>
 *     <ul data-cp-track> <li>…</li> … </ul>
 *     <button data-cp-prev>…</button> <button data-cp-next>…</button>   (opcional)
 *     <div class="cp-dots"> <button data-cp-dot aria-label="…"></button> … </div>   (opcional)
 *   </cp-carousel>
 *
 * - autoplay: segundos entre slides; pausa no hover, no foco e com prefers-reduced-motion.
 * - loop: setas e autoplay voltam ao início/fim em vez de desabilitar a seta.
 */
class CpCarousel extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {number | undefined} */
  #timer;
  #paused = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // update() é barato (poucos itens): roda direto no scroll, sem depender de rAF (pausado em abas ocultas)
    this.track?.addEventListener('scroll', () => this.update(), { passive: true, signal });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index), { signal });
    });

    this.querySelector('[data-cp-prev]')?.addEventListener('click', () => this.step(-1), { signal });
    this.querySelector('[data-cp-next]')?.addEventListener('click', () => this.step(1), { signal });

    if (this.autoplay > 0 && this.items.length > 1) {
      const pause = () => {
        this.#paused = true;
      };
      const resume = () => {
        this.#paused = false;
      };
      this.addEventListener('pointerenter', pause, { signal });
      this.addEventListener('pointerleave', resume, { signal });
      this.addEventListener('focusin', pause, { signal });
      this.addEventListener('focusout', resume, { signal });
      this.#start();
    }

    this.update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    window.clearInterval(this.#timer);
  }

  /** @returns {number} */
  get autoplay() {
    return Number(this.getAttribute('autoplay')) || 0;
  }

  /** @returns {boolean} */
  get loop() {
    return this.hasAttribute('loop');
  }

  /** @returns {HTMLElement | null} */
  get track() {
    return this.querySelector('[data-cp-track]');
  }

  /** @returns {HTMLElement[]} */
  get items() {
    return this.track ? /** @type {HTMLElement[]} */ ([...this.track.children]) : [];
  }

  /** @returns {HTMLElement[]} */
  get dots() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-cp-dot]')]);
  }

  /** Índice do item mais próximo do início da área visível. */
  get current() {
    const track = this.track;
    if (!track) return 0;
    let current = 0;
    let closest = Infinity;
    this.items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - track.scrollLeft);
      if (distance < closest) {
        closest = distance;
        current = index;
      }
    });
    return current;
  }

  get atStart() {
    return (this.track?.scrollLeft ?? 0) <= 2;
  }

  get atEnd() {
    const track = this.track;
    return !track || track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  }

  /** @param {number} index */
  goTo(index) {
    const item = this.items[index];
    if (!item || !this.track) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: item.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @param {number} direction 1 = próximo, -1 = anterior */
  step(direction) {
    const count = this.items.length;
    if (!count) return;

    if (direction > 0 && this.atEnd) {
      if (this.loop) this.goTo(0);
      return;
    }
    if (direction < 0 && this.atStart) {
      if (this.loop) this.goTo(count - 1);
      return;
    }
    this.goTo(Math.min(Math.max(this.current + direction, 0), count - 1));
  }

  #start() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#timer = window.setInterval(() => {
      if (!this.#paused && document.visibilityState === 'visible') this.step(1);
    }, this.autoplay * 1000);
  }

  update() {
    const current = this.current;

    this.dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', index === current ? 'true' : 'false');
    });

    if (!this.loop) {
      this.querySelector('[data-cp-prev]')?.toggleAttribute('disabled', this.atStart);
      this.querySelector('[data-cp-next]')?.toggleAttribute('disabled', this.atEnd);
    }
  }
}

if (!customElements.get('cp-carousel')) {
  customElements.define('cp-carousel', CpCarousel);
}
